/**
 * Basic translation map for UI copy.
 * Extend this object to add new keys, grouped by feature (alphabetical).
 */
export const translations = {
	refresh: {
		refreshing: 'Refreshing',
		failed: 'Refresh failed. Please try again.',
		retry: 'Retry',
	},
	auth: {
		loginButton: 'Sign in via Discogs',
		loginFailed: 'Discogs sign-in failed. Please try again.',
		sessionExpired: 'Your session expired. Please sign in again.',
		sessionRefreshFailed:
			'Your session could not be refreshed. Please sign in again.',
		loginTitle: 'Login',
	},
	onboarding: {
		carouselLabel: 'Tunetoss features',
		createDiscogsAccount: 'Create an account',
		dotsLabel: 'Choose a feature',
		newToDiscogs: 'New to Discogs?',
		next: 'Next feature',
		previewNotice: 'Previews are simplified illustrations of the app.',
		previous: 'Previous feature',
		slideLabel: '%{index} of %{total}',
		welcome: {
			title: 'Rediscover your record collection',
			body: 'Tunetoss connects to your Discogs collection and helps you pick what to play next, one shuffle at a time.',
		},
		shuffle: {
			title: 'Can’t decide what to play?',
			body: 'Set a few filters, hit shuffle and let Tunetoss pick a record from your shelf.',
		},
		browse: {
			title: 'Your whole collection in one place',
			body: 'Search and filter your releases and see detailed release information at a glance.',
		},
		history: {
			title: 'Log every spin',
			body: 'Track when you listen and see how often and when each record was last played.',
		},
		fresh: {
			title: 'Give records a break',
			body: 'Hide a record from shuffle for a few months after you play it, so the same picks don’t keep coming back.',
		},
		friends: {
			title: 'See what friends are spinning',
			body: 'Add friends and follow their listens, likes, ratings and new additions in Activity.',
		},
	},
	collection: {
		syncChecking: 'Checking Discogs',
		syncNow: 'Check Discogs for changes',
		syncedAt: 'Synced %{time}',
		layoutLabel: 'List layout',
		layoutSwitch: 'Switch to',
		layout_default: 'Default',
		layout_compact: 'Compact',
		layout_cover: 'Cover only',
		groupToday: 'Today',
		groupYesterday: 'Yesterday',
		groupLast7Days: 'Last 7 days',
		groupLast30Days: 'Last 30 days',
		groupFuture: 'Future',
		groupNoAddedDate: 'No added date',
		groupNoListeningDate: 'No listening date',
		importNotice:
			'The full collection import might take a few minutes because master years and details need extra API calls, and Discogs limits requests per minute. You can leave this page — syncing continues in the background.',
		title: 'Collection',
		dateAddedLabel: 'Added:',
		filters: 'Filters',
		filtersActive: 'Filters active',
		folder: 'Folder',
		folderAll: 'Any folder',
		collectionFallback:
			"We couldn't load your Discogs collection. If your collection is private, Discogs only lets Tunetoss import it when your account connection is valid. Reconnect Discogs or make the collection public, then try again.",
		media: 'Format',
		mediaAll: 'Any format',
		mediaCassette: 'Cassette',
		mediaCD: 'CD',
		mediaBoxSet: 'Box Set',
		mediaSingle: 'Single',
		mediaVinyl: 'Vinyl',
		ratingFilter: 'Rating',
		ratingAny: 'Any rating',
		ratingUnrated: 'Unrated',
		ratingStar: 'star',
		ratingStars: 'stars',
		statusFilter: 'Status',
		searchFilter: 'Search {filter}…',
		clearFilterSearch: 'Clear search',
		removeFilterValue: 'Remove {value}',
		noFilterMatches: 'No matching options',
		genreAny: 'Any genre',
		styleAny: 'Any style',
		valueLabel: 'Estimated collection value',
		valueMaximum: 'Max',
		valueMedian: 'Median',
		valueMinimum: 'Min',
		historyAll: 'Any status',
		historyOnly: 'Listened',
		historyNotListened: 'Not listened',
		historyHidden: 'Hidden',
		noItems: 'No items match this filter.',
		search: 'Search',
		searchPlaceholder: 'Search collection',
		sync: 'Sync collection',
		syncFailed: 'Could not refresh collection. Please try again.',
		sortAdded: 'Added',
		sortArtist: 'Artist',
		sortBy: 'Sort by',
		sortListened: 'Listened',
		sortMasterYear: 'Master year',
		sortRating: 'Rating',
		sortReleaseYear: 'Release year',
		sortReverseDirection: 'Reverse sort direction',
		sortTitle: 'Title',
		dimReleasesFilter: 'Dim releases',
		undimReleasesFilter: 'Undim releases',
	},
	common: {
		failed: 'Failed to load.',
		favoritesOnly: 'Favorites only',
		item: 'item',
		items: 'items',
		listEnd: "You've reached the end.",
		all: 'All',
		reset: 'Reset',
		resetFilters: 'Reset filters',
		saveAsDefault: 'Save as default',
		setAsDefaultView: 'Set as default view',
		resetToDefault: 'Reset to default',
	},
	dialog: {
		add: 'Add',
		cancel: 'Cancel',
		errorGeneric: 'Something went wrong.',
		ok: 'OK',
		remove: 'Remove',
		save: 'Save',
		successGeneric: 'Saved.',
	},
	historyPage: {
		dateAsc: 'Date ↑',
		dateDesc: 'Date ↓',
		listTab: 'Releases',
		sortBy: 'Sort by',
		statsPlaceholder: 'Statistics are coming soon.',
		statsTab: 'Statistics',
	},
	nav: {
		back: 'Back',
		showOnDiscogs: 'Show on Discogs',
	},
	release: {
		addDate: 'Add date',
		listeningHistory: 'Listening history',
		emptyHistory: 'No listening dates yet.',
		newHistoryDate: 'New',
		undoHistoryRemoval: 'Undo',
		removeAllHistory: 'Remove all',
		historySaveHint: 'Changes apply when you save.',
		addDateLabel: 'Add listening dates',
		edit: 'Edit Release',
		editButtonLabel: 'Edit history and hidden status',
		editTitle: 'Edit Release',
		addedLabel: 'Added:',
		lastHeardLabel: 'Last heard:',
		format: 'Format',
		hide: 'Hide',
		hideButtonLabel: 'Hide release until later',
		hideFor: 'Hide from shuffle for',
		hideForever: 'Forever',
		hideLabel: 'Hide release until later',
		hidePlaceholder: "Don't hide",
		hiddenUntilLabel: 'Hidden until:',
		history: 'History',
		releaseInfos: 'Release Infos',
		historyDates: 'History dates',
		info: 'Info',
		tracklist: 'Tracklist',
		listen: 'Log play',
		listenButtonLabel: 'Log play',
		addFavorite: 'Add to favorites',
		favoriteLabel: 'Favorite',
		removeFavorite: 'Remove from favorites',
		favoriteSaveFailed: 'Could not update favorite.',
		listenedOn: 'Listened on',
		listenTitle: 'Log a play',
		unknownDate: 'Exact date unknown',
		unknownDateSummary: 'Unknown date',
		loading: 'Loading ...',
		load: 'load',
		genreLabel: 'Genre:',
		labelLabel: 'Label(s):',
		noRecord: 'No Record',
		byLabel: 'by',
		countLabel: 'Count:',
		styleLabel: 'Style:',
		closeRatingEditor: 'Close rating editor',
		discogsRating: 'Discogs rating',
		editRating: 'Edit your rating',
		rateOutOfFive: 'Rate %{rating} out of 5',
		rating: 'rating',
		ratingsCount: 'ratings',
		ratingSaveFailed: 'Could not save your rating.',
		removeRating: 'Remove rating',
		yourRating: 'Your rating',
		remove: 'Remove date',
		removeLabel: 'Remove dates from history',
		removeAll: 'All',
		removeDefault: 'No selection',
		retry: 'retry',
		similarItems: 'Similar releases',
		sameArtistItems: 'Same artist(s)',
		sameLabelItems: 'Same label(s)',
		tracklistFallback: 'No tracklist available',
		futureDateError: 'Date cannot be in the future.',
	},
	progress: {
		importing: 'Importing Discogs collection',
		importingHelp:
			'Tunetoss keeps your collection up to date while you use it. After %{days} days away, it imports your collection again.',
		loading: 'Loading',
	},
	releaseNotes: {
		dialogTitle: "What's new in Tunetoss?",
		dismiss: 'Dismiss',
		empty: 'No release notes yet.',
		footerLink: 'Settings under Help & About',
		footerPrefix: 'Release notes are always available in',
		intro: 'All Tunetoss release notes are listed here.',
		later: 'Later',
		newRelease: 'New release',
		viewAll: 'View release notes',
	},
	settings: {
		title: 'Settings',
		appSettings: 'App settings',
		appearance: 'Appearance',
		collection: 'Collection',
		shuffle: 'Shuffle',
		account: 'Account',
		defaultHide: 'Default hide duration',
		defaultHideNote: 'When opening the "Log play" dialog',
		appSettingsFeedbackPrompt: 'Missing something?',
		appSettingsFeedbackAction: 'Leave feedback or a suggestion.',
		skipShuffleAnimation: 'Skip shuffle animation',
		haptics: 'Haptic feedback',
		showCollectionValue: 'Show collection value',
		historyImportLabel: 'Import release data',
		historyImportAction: 'Import',
		historyData: 'Release data',
		historyExportLabel: 'Export release data',
		historyExportButton: 'Export',
		historySampleLabel: 'Sample CSV + instructions (ZIP)',
		historySampleButton: 'Download',
		historyImportPreviewTitle: 'Import release data',
		historyImportPreviewIntro: 'Review %{filename} before importing:',
		historyImportRows: 'Release rows',
		historyImportReleases: 'Releases',
		releaseImportFavorites: 'Favorite values to apply',
		releaseImportInterrupted:
			'Import interrupted. Some rows may have been saved. You can retry the same file safely; merged listens will not be duplicated.',
		releaseImportHidden: 'Hidden-status values to apply',
		releaseImportSkippedFavorites:
			'Favorites skipped (not in current collection)',
		releaseImportMergeNotice:
			'Import merges listening history; it does not delete existing listens or releases. Daily listen counts and unknown-date counts keep the higher value, so importing the same file again does not duplicate plays. Blank cells and omitted columns keep existing values. Explicit favorite and hidden-status values replace those settings only for the listed releases.',
		releaseImportFavoriteNotice:
			'Favorites apply to all current collection copies of each listed release. Favorites outside your current collection are skipped. Import never writes to Discogs or changes ratings. A later sync can replace favorites if you use a Discogs Favorite field. Imported listens stay out of Activity; imported favorites can appear to accepted friends.',

		historyImportDated: 'Dated listens',
		historyImportUnknown: 'Unknown-date listens',
		historyImportDuplicates: 'Duplicate dated listens',
		historyImportNew: 'New dated listens',
		historyImportConfirm: 'Import',
		historyImportSuccess:
			'Merged %{releases} releases: added %{dated} dated and %{unknown} unknown-date listens.',
		historyImportFileTooLarge: 'The CSV file must be 1 MB or smaller.',
		historyImportValidationError: 'CSV row %{row}: %{message}',
		historyExportSuccess: 'Release data export downloaded.',
		historyFileShared: 'File saved or shared.',
		historySampleSuccess:
			'Sample ZIP downloaded. Extract it for the CSV and README instructions.',
		general: 'General',
		helpAbout: 'Help & About',
		support: 'Support',
		history: 'History',
		homepage: 'Homepage',
		homepageCollection: 'Collection',
		homepageShuffle: 'Shuffle',
		lightMode: 'Light mode',
		logout: 'Logout',
		resetHistory: 'Reset',
		resetHistoryConfirm:
			'Delete all listening dates and counts, clear hidden-release flags and timers, and remove your listened Activity entries? Favorites, ratings, friends, settings, and your Discogs collection are kept.\n\nThis cannot be undone. Type "reset" to continue.',
		resetHistoryLabel: 'Reset listening data',
		resetHistoryHelpLabel: 'What does reset listening data remove?',
		resetHistoryHelp:
			'Deletes all your listening dates and counts. Clears hidden-release flags and timers. Removes your listened Activity entries. Keeps favorites, ratings, other Activity entries, friends, app settings, your account, and your Discogs collection. This cannot be undone.',
		resetHistorySuccess: 'Listening data reset.',
		deleteAccount: 'Delete',
		deleteAccountLabel: 'Delete Tunetoss account permanently',
		deleteConfirm:
			'Delete your account permanently? This cannot be undone. Account actions will stop while deletion completes. Interrupted cleanup is retried automatically.\n\nThis cannot be undone. Type "delete" to continue.',
		contactFeedback: 'Support & Feedback',
		contactFeedbackIntro:
			'Ask for help, share feedback or ideas, or just say hello.',
		contactFeedbackPlaceholder: '',
		email: 'Email',
		sendCopy: 'Send me a copy',
		emailAlternative: 'Prefer to use your email app? Write to',
		supportCopyFailed:
			'Your message reached support, but we could not send your copy. Please do not resubmit; contact us by email if you need a copy.',
		emailPlaceholder: 'you@example.com',
		message: 'Message',
		sendMessage: 'Send message',
		reportBug: 'Report a Bug',
		reportBugIntro:
			'The more detail you include, the faster we can fix it.',
		bugDescription: 'Bug report',
		bugReportPlaceholder:
			'What did you do?\nWhat did you expect?\nWhat happened instead?\nSteps to reproduce:',
		bugReportContextNotice:
			'Device and app info (version, platform, screen, language, last page) is included to help us fix the issue.',
		submitBugReport: 'Submit bug report',
		supportSubmitError:
			'Could not send your message. Please try again later.',
		supportRateLimited:
			'You have sent several messages recently. Please try again in an hour or contact us by email.',
		supportSubmitSuccess: 'Thanks, your message was sent.',
		whatsNew: "What's New",
		privacyPolicy: 'Privacy Policy',
		termsService: 'Terms of Service',
	},
	shuffle: {
		anyYear: 'Any year',
		chooseOption: 'Please choose an option',
		findNextSpin: 'Find your next spin',
		folder: 'Folder',
		genre: 'Genre',
		includeHiddenHelp:
			'Hidden releases are excluded from shuffle until their hidden period ends. A release can be hidden via the releases Log play or Edit Release dialog.',
		includeHidden: 'Include Hidden',
		shuffle: 'Shuffle',
		stopAnimation: 'Stop',
		type: 'Type',
		year: 'Year',
		yearFrom: 'From',
		yearUntil: 'Until',
	},
}

/**
 * Translation helper: returns the string for a dotted key, or the key itself if missing.
 */
export function t(path: string): string {
	const parts = path.split('.')
	let current: unknown = translations
	for (const part of parts) {
		if (
			typeof current === 'object' &&
			current !== null &&
			Object.hasOwn(current, part)
		) {
			current = (current as Record<string, unknown>)[part]
		} else {
			return path
		}
	}
	return typeof current === 'string' ? current : path
}
